// @file Component to render the background image of the whiteboard frame
import { tailwindFullConfig } from '@@/bits/tailwind_config'
import { createBridgeToReactState } from '@@/bits/vue_react_bridge'
import type { PageMeta } from '@@/bits/whiteboard'
import { BackgroundType, STANDARD_BACKGROUND_TYPES } from '@@/bits/whiteboard'
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '@@/bits/whiteboard_no_dependencies'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { useMemo } from 'react'
import { track, useEditor } from 'tldraw'
import { computed } from 'vue'

const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()

const useDisplayedFrameBackgrounds = createBridgeToReactState(
  computed(() => whiteboardFrameBackgroundStore.displayedFrameBackgrounds),
)

const WhiteboardFrameBackground = track(({ style = {} }: { style?: React.CSSProperties }): React.JSX.Element => {
  const [displayedFrameBackgrounds] = useDisplayedFrameBackgrounds()
  const editor = useEditor()
  const currentPage = editor.getCurrentPage()
  const currentPageMeta = useMemo((): PageMeta => currentPage.meta, [currentPage])
  const currentBackgroundType = displayedFrameBackgrounds?.[currentPage.id]?.id ?? currentPageMeta.backgroundType
  const currentBackgroundImageUrl =
    displayedFrameBackgrounds?.[currentPage.id]?.url ?? whiteboardFrameBackgroundStore.getPageBackgroundUrl(currentPage)
  const backgroundStyle: React.CSSProperties =
    currentBackgroundType === BackgroundType.Custom
      ? {
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
        }
      : {}
  const isTransparentBackground = currentBackgroundType === BackgroundType.Frosted
  const isStandardBackground = STANDARD_BACKGROUND_TYPES.includes(currentBackgroundType as BackgroundType)
  const isContainBackground = !isStandardBackground

  // Some backgrounds are smaller than the canvas size, which we'll repeat to fill the canvas.
  // Since this component is rendered in Tldraw's Background component which has width: 100% and height: 100%,
  // we need to set the background size based on the image's dimensions to achieve the ideal repeat density.
  // TODO: Talk to designers to change all backgrounds to 1920x1080 to avoid having to do this.
  const customBackgroundSize = useMemo<'auto' | `calc(${string}) calc(${string})`>(() => {
    if (isContainBackground) return 'auto'
    const { width, height } = displayedFrameBackgrounds?.[currentPage.id] ?? {}
    if (width == null || height == null) return 'auto'
    const widthRatio = CANVAS_WIDTH / width
    const heightRatio = CANVAS_HEIGHT / height
    return `calc(100% / ${widthRatio}) calc(100% / ${heightRatio})`
  }, [isContainBackground, displayedFrameBackgrounds, currentPage.id])

  return (
    <div
      style={{
        ...style,
        backgroundImage: `url(${currentBackgroundImageUrl})`,
        backgroundRepeat: 'repeat',
        backgroundSize: isContainBackground ? 'contain' : customBackgroundSize,
        backgroundColor: isTransparentBackground ? 'transparent' : tailwindFullConfig.theme.colors.grey[50],
        backdropFilter: isTransparentBackground ? 'blur(12px)' : '',
        ...backgroundStyle,
        pointerEvents: 'none',
      }}
      data-testid="whiteboardFrameBackground"
    ></div>
  )
})

export default WhiteboardFrameBackground
